import React from 'react';
import { Paper, makeStyles, Theme, Icon } from '@material-ui/core';
import clsx from 'clsx';
import { useModePanelStyle, CompPanelHeader, BaseCompProps } from '@moderepo/mode-app-components';
import { AggregatedMetricData, LabelPosition, WidgetDataViewLayout, ValueDisplaySize, ValueThresholdMonitorSeverity, ValueThresholdReachedInfo,
    ValueThresholdReachingType } from '../../containers';
import { NO_VALUE } from '../../utils/constants';



const useStyle = makeStyles((theme: Theme) => {

    // The base font-size for the value display. This is the size we will use when valueDisplaySize = 1.
    const valueDisplaySizeBase = {
        xs: 20,
        md: 25,
        lg: 30,
    };

    // If multiplier is 4 then it mean each size will be incremented by 4.
    // For example: If size 1 = 20px then size 2 = 24px and then size 3 = 28px and so on
    const valueDisplaySizeMultiplier = {
        xs: 2,
        md: 3,
        lg: 4,
    };

    // Build the font-size variables to be used in the CSS
    const fontSizeVariables = {
        xs: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].reduce((result, value, index) => {
            return {
                ...result,
                [`&.value-display-size-${value}`]: {
                    '--font-size': `${valueDisplaySizeBase.xs + (index * valueDisplaySizeMultiplier.xs)}px`,
                },
            };
        }, {}),
        md: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].reduce((result, value, index) => {
            return {
                ...result,
                [`&.value-display-size-${value}`]: {
                    '--font-size': `${valueDisplaySizeBase.md + (index * valueDisplaySizeMultiplier.md)}px`,
                },
            };
        }, {}),
        lg: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].reduce((result, value, index) => {
            return {
                ...result,
                [`&.value-display-size-${value}`]: {
                    '--font-size': `${valueDisplaySizeBase.lg + (index * valueDisplaySizeMultiplier.lg)}px`,
                },
            };
        }, {}),
    };



    return {
        root: {
            overflow : 'hidden',
            height   : 500,
            minHeight: 'unset',
        },

        panelContent: {
            padding      : 0,
            height       : '100%',
            display      : 'flex',
            flexDirection: 'column',
        },

        valuesContainer: {
            display       : 'flex',
            alignItems    : 'center',
            justifyContent: 'center',
            width         : '100%',
            height        : '100%',
            overflow      : 'auto',

            [`&.layout-${WidgetDataViewLayout.VERTICAL}`]: {
                flexDirection: 'column',
            },
            [`&.layout-${WidgetDataViewLayout.HORIZONTAL}`]: {
                flexDirection: 'row',
            },
        },

        valueContainer: {
            flex          : 1,
            margin        : theme.spacing(1),
            display       : 'flex',
            flexDirection : 'column',
            alignItems    : 'center',
            justifyContent: 'center',

            '& .value-and-unit': {
                padding      : theme.spacing(1),
                color        : theme.palette.text.primary,
                display      : 'flex',
                flexDirection: 'row',
                alignItems   : 'baseline',
                '& .value'   : {
                    fontWeight: 'bold',
                    fontSize  : 30,
                },
                '& .unit': {
                    marginLeft: 5,
                    fontSize  : 15,
                },
                '& .value-change-icon': {
                    fontSize  : 15,
                    marginLeft: 5,
                    color     : theme.palette.text.secondary,
                    alignSelf : 'center',
                    '&.up'    : {
                        transform: 'rotateZ(0deg)',
                        animation: `$animate-increasing 1000ms 2 ${theme.transitions.easing.easeInOut}`,
                    },
                    '&.down': {
                        transform: 'rotateZ(180deg)',
                        animation: `$animate-decreasing 1000ms 2 ${theme.transitions.easing.easeInOut}`,
                    },
                },
                '& .threshold-reached-icon-wrapper': {
                    fontSize      : 40,
                    marginRight   : 5,
                    display       : 'flex',
                    justifyContent: 'center',
                    height        : '100%',
                    position      : 'relative',
                    width         : 'calc(var(--font-size)/2)',

                    '& .threshold-reached-icon': {
                        position: 'absolute',
                    },

                    [`&.type-${ValueThresholdReachingType.TOO_LOW}`]: {
                        alignSelf                  : 'flex-end',
                        borderBottom               : '5px solid #000000',
                        '& .threshold-reached-icon': {
                            bottom   : 0,
                            transform: 'rotateZ(90deg)',
                            animation: `$animate-threshold-low 2000ms infinite ${theme.transitions.easing.easeInOut}`,
                        },
                    },
                    [`&.type-${ValueThresholdReachingType.TOO_HIGH}`]: {
                        alignSelf                  : 'flex-start',
                        borderTop                  : '5px solid #000000',
                        '& .threshold-reached-icon': {
                            top      : 0,
                            transform: 'rotateZ(-90deg)',
                            animation: `$animate-threshold-high 2000ms infinite ${theme.transitions.easing.easeInOut}`,
                        },
                    },
                    [`&.severity-${ValueThresholdMonitorSeverity.WARNING}`]: {
                        color      : theme.palette.warning.main,
                        borderColor: theme.palette.warning.main,
                    },
                    [`&.severity-${ValueThresholdMonitorSeverity.CRITICAL}`]: {
                        color      : theme.palette.error.main,
                        borderColor: theme.palette.error.main,
                    },
                },
            },

            '& .label': {
                fontSize  : 15,
                color     : theme.palette.text.secondary,
                textAlign : 'center',
                fontWeight: 500,
                width     : '100%',
            },

            '& .date': {
                fontSize  : 13,
                color     : theme.palette.text.primary,
                textAlign : 'center',
                fontWeight: 'normal',
                width     : '100%',
            },

            '&.label-position-bottom': {
                flexDirection: 'column-reverse',
            },

            '&.label-position-none': {
                '& .label': {
                    display: 'none',
                },
            },

            // Add the CSS variables for the font size. We will use these variables later
            ...fontSizeVariables.xs,
            [theme.breakpoints.up('md')]: {
                ...fontSizeVariables.md,
            },
            [theme.breakpoints.up('lg')]: {
                ...fontSizeVariables.lg,
            },

            '&[class*="value-display-size"]': {
                '& .value-and-unit': {
                    '& .value': {
                        fontSize: 'var(--font-size)',
                    },
                    '& .unit': {
                        fontSize: 'calc(var(--font-size) / 2)',
                    },
                    '& .value-change-icon': {
                        fontSize: 'calc(var(--font-size) / 2)',
                    },
                    '& .threshold-reached-icon': {
                        fontSize: 'calc(var(--font-size) * 1.2)',
                    },
                },
                '& .label': {
                    fontSize: 'calc(var(--font-size) * 0.5)',
                },
                '& .date': {
                    fontSize: 'calc(var(--font-size) * 0.4)',
                },
            },

            '&.has-threshold-monitor': {
                '& .value-and-unit': {
                    color: `${theme.palette.success.main} !important`,    // this is 'important' and will override all other color settings
                },
            },

            '&.has-threshold-alert': {
                [`&.severity-${ValueThresholdMonitorSeverity.WARNING}`]: {
                    '& .value-and-unit': {
                        color: `${theme.palette.warning.main} !important`,    // this is 'important' and will override all other color settings
                    },
                },
                [`&.severity-${ValueThresholdMonitorSeverity.CRITICAL}`]: {
                    '& .value-and-unit': {
                        color: `${theme.palette.error.main} !important`,    // this is 'important' and will override all other color settings
                    },
                },
            },
        },


        [theme.breakpoints.up('md')]: {
            valueContainer: {
                margin: theme.spacing(2),
            },
            updatedDate: {
                fontSize: 'larger',
            },
        },

        '@keyframes animate-flashing': {
            '0%': {
                opacity: 1,
            },
            '20%': {
                opacity: 0,
            },
        },

        '@keyframes animate-increasing': {
            '0%': {
                marginTop: 'calc(var(--font-size)/2)',
            },
            '50%': {
                marginTop: 0,
            },
            '100%': {
                marginTop: 0,
            },
        },

        '@keyframes animate-decreasing': {
            '0%': {
                marginBottom: 'calc(var(--font-size)/2)',
            },
            '50%': {
                marginBottom: 0,
            },
            '100%': {
                marginBottom: 0,
            },
        },

        '@keyframes animate-threshold-high': {
            '0%': {
                top: 'calc(var(--font-size)/2)',
            },
            '50%': {
                top: 0,
            },
            '100%': {
                top: 0,
            },
        },

        '@keyframes animate-threshold-low': {
            '0%': {
                bottom: 'calc(var(--font-size)/2)',
            },
            '50%': {
                bottom: 0,
            },
            '100%': {
                bottom: 0,
            },
        },
    };
}, {
    name: 'ValueDashboardWidgetDataViewComp', index: 1,
});



export interface ValueDashboardWidgetDataViewCompProps extends BaseCompProps {
    readonly layout: WidgetDataViewLayout;
    readonly valueDisplaySize?: ValueDisplaySize | undefined;
    readonly valueDisplayColor?: string | undefined;
    readonly showLabel?: LabelPosition | undefined;

    // The data for the metrics
    readonly aggregatedData: AggregatedMetricData;

    // Localized metrics' data used for display
    readonly localizedAggregatedData?: {
        readonly [metricName: string]: string;
    } | undefined;

    // The previous data point. We can use this to show whether the value increased or decreased
    readonly previousAggregatedData: AggregatedMetricData | undefined;

    /**
     * The status of threshold monitor ONLY if this widget has monitor threshold enabled. If not, this should be set to undefined. If this is NOT
     * undefined, we will assume this widget has threshold monitor enabled. Since there can be more than 1 metrics displayed
     * and not all metrics reached threshold, this will be a map of metricName => threshold reaching info for only the metric that
     * reached the threshold. e.g.
     *      {
     *          temperature: {
     *              type: 'too_high', severity: 'critical'      // this mean the 'temperature' reached the threshold
     *          },
     *          pressure: undefined,                            // this mean the 'pressure' value is fine
     *      }
     */
    readonly thresholdMonitorStatus?: {
        readonly [metricValueName: string]: ValueThresholdReachedInfo | undefined;
    } | undefined;

    // The component to show when there is no data
    readonly noDataMessageComp: React.ReactNode;
}



/**
 * This is the UI/Data View for the ValueDashboardWidget. This component will display the time series values as 1 SINGLE VALUE.
 */
export const ValueDashboardWidgetDataViewComp: React.FC<ValueDashboardWidgetDataViewCompProps> = (props: ValueDashboardWidgetDataViewCompProps) => {
    const panelClasses = useModePanelStyle();
    const classes = useStyle();

    const { aggregatedData, previousAggregatedData } = props;


    return (
        <Paper
            elevation={2}
            className={clsx(
                panelClasses.root, classes.root, props.className, props.showCustomActionOnHover && 'show-custom-action-on-hover',
            )}
        >
            <CompPanelHeader {...props} />

            <div className={clsx(panelClasses.panelContent, classes.panelContent)}>

                {aggregatedData && aggregatedData.numericMetrics && aggregatedData.numericMetrics.length > 0 && (
                    <>
                        <div className={clsx(classes.valuesContainer, `layout-${props.layout}`)}>
                            {aggregatedData.numericMetrics.map((metric) => {

                                const metricDataPoint = aggregatedData.numericMetricData?.[metric.name];
                                const numericValue = metricDataPoint?.value ?? undefined;
                                const stringValue = props.localizedAggregatedData?.[metric.name] ?? numericValue?.toString() ?? NO_VALUE;
                                const metricPrevDataPoint = previousAggregatedData?.[metric.name];

                                // Find the difference between the currentValue and previousValue so we can show up/down arrow
                                const diff = metricPrevDataPoint?.value !== undefined && metricPrevDataPoint?.value !== null
                                    && metricDataPoint?.value !== undefined && metricDataPoint?.value !== null
                                    ? metricDataPoint.value - metricPrevDataPoint.value
                                    : 0;

                                const thresholdAlertInfo = props.thresholdMonitorStatus?.[metric.name];

                                return (
                                    <div
                                        key={metric.name}
                                        className={clsx(
                                            classes.valueContainer,
                                            `value-display-size-${props.valueDisplaySize || 5}`,
                                            `label-position-${props.showLabel}`,
                                            props.thresholdMonitorStatus && 'has-threshold-monitor',
                                            thresholdAlertInfo
                                                && `has-threshold-alert severity-${thresholdAlertInfo.severity} type-${thresholdAlertInfo.type}`,
                                        )}
                                    >
                                        <div className={clsx('label')}>
                                            {metric.displayName || metric.name}
                                        </div>
                                        <div className={clsx('value-and-unit')} style={{ color: props.valueDisplayColor }}>
                                            {thresholdAlertInfo && (
                                                <div className={clsx(
                                                    'threshold-reached-icon-wrapper',
                                                    `severity-${thresholdAlertInfo.severity}`,
                                                    `type-${thresholdAlertInfo.type}`,
                                                )}
                                                >
                                                    <Icon className={clsx('threshold-reached-icon')}>
                                                        trending_flat
                                                    </Icon>
                                                </div>
                                            )}
                                            <div
                                                className={clsx('value')}
                                            >
                                                {stringValue}
                                            </div>
                                            {metric.unit && (
                                                <div className={clsx('unit')}>{metric.unit}</div>
                                            )}
                                            {diff !== 0 && (
                                                <Icon className={clsx('value-change-icon', diff > 0 && 'up', diff < 0 && 'down')}>
                                                    arrow_upward
                                                </Icon>
                                            )}
                                        </div>

                                        {metricDataPoint?.dateString && (
                                            <div className="date">
                                                {metricDataPoint.dateString}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}

                {/*
                    If there are no metric or no data then show empty data message
                */}
                {(!props.aggregatedData || !props.aggregatedData.numericMetrics || props.aggregatedData.numericMetrics.length === 0)
                    && props.noDataMessageComp}
            </div>
        </Paper>
    );
};
